import { useMemo } from "react";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useLanguage } from "@/contexts/LanguageContext";
import { formatNumber } from "@/utils/numberFormatter";
import { useOriginProduct } from "@/hooks/useOriginProduct";
import { FootprintProduct } from "@/types/results";

interface DistributionCarbonChartData {
  name: string;
  value: number;
  percentage: number;
  color: string;
  breakdown?: {
    materials?: number;
    ecr?: number;
  };
}

interface DistributionCarbonPieChartProps {
  product: FootprintProduct;
  className?: string;
}

export const DistributionCarbonPieChart = ({
  product,
  className,
}: DistributionCarbonPieChartProps) => {
  const { t } = useLanguage();

  // Fetch origin product data if needed (for transformation products from studied quarries)
  const { originProduct } = useOriginProduct(
    product.origin_company_id || null,
    product.origin_product_id || null
  );

  // Generate chart data based on activity type and distribution data
  const chartData = useMemo((): DistributionCarbonChartData[] => {
    const data: DistributionCarbonChartData[] = [];
    
    // Get distribution data from product
    const distribution = product.distribution;
    if (!distribution) return [];

    const activityType = product.product_flow || "extraction";
    const annualProduction = product.annual_production || 0;

     // Get distribution footprint - these are per-unit values, multiply by annual production
     const distributionFootprint = (distribution.carbon_footprint?.total_kgCO2e || 0) * annualProduction;

    // Check if this product comes from an already studied quarry
    const hasStoredOriginData = product.origin_extraction_carbon_footprint_total !== undefined;
    const comesFromStudiedQuarry = product.comes_from_associated === "yes" && 
                                  (hasStoredOriginData || originProduct);

    if (activityType === "extraction") {
      // Extraction / Extraction and Transformation (Mixed) Company
      // For extraction products, materials + electricity represents the extraction process
      const extractionFootprint = (product.carbon_footprint_materials || 0) + (product.carbon_footprint_electricity || 0);
      const extractionFootprintTotal = extractionFootprint * annualProduction;
      const totalDisplayed = extractionFootprintTotal + distributionFootprint;

      if (totalDisplayed > 0) {
        if (extractionFootprintTotal > 0) {
          data.push({
            name: t("results.distribution.carbon.phases.extraction"),
            value: extractionFootprintTotal,
            percentage: (extractionFootprintTotal / totalDisplayed) * 100,
            color: "#14b8a6",
            breakdown: {
              materials: (product.carbon_footprint_materials || 0) * annualProduction,
              ecr: (product.carbon_footprint_electricity || 0) * annualProduction,
            },
          });
        }
        
        if (distributionFootprint > 0) {
          data.push({
            name: t("results.distribution.carbon.phases.distribution"),
            value: distributionFootprint,
            percentage: (distributionFootprint / totalDisplayed) * 100,
            color: "#0891b2",
          });
        }
      }
    } else if (activityType === "transformation") {
      // Transformation / Extraction and Transformation (Mixed) Company
      // For transformation products, materials + electricity represents the TRANSFORMATION process
      const transformationFootprint = (product.carbon_footprint_materials || 0) + (product.carbon_footprint_electricity || 0);
      const transformationFootprintTotal = transformationFootprint * annualProduction;
      
      const transportFootprint = (product.carbon_footprint_transport || 0) * annualProduction;
      
      // Get extraction footprint - use origin product data if available
      let extractionFootprint = 0;
      if (comesFromStudiedQuarry) {
        if (hasStoredOriginData) {
          extractionFootprint = product.origin_extraction_carbon_footprint_total || 0;
        } else if (originProduct) {
          extractionFootprint = originProduct.carbon_footprint_total || 0;
        }
      }
      
      const totalDisplayed = extractionFootprint + transformationFootprintTotal + transportFootprint + distributionFootprint;

      if (totalDisplayed > 0) {
        if (extractionFootprint > 0) {
          data.push({
            name: t("results.distribution.carbon.phases.extraction"),
            value: extractionFootprint,
            percentage: (extractionFootprint / totalDisplayed) * 100,
            color: "#14b8a6",
          });
        }
        
        if (transformationFootprintTotal > 0) {
          data.push({
            name: t("results.distribution.carbon.phases.transformation"),
            value: transformationFootprintTotal,
            percentage: (transformationFootprintTotal / totalDisplayed) * 100,
            color: "#8b5cf6",
            breakdown: {
              materials: (product.carbon_footprint_materials || 0) * annualProduction,
              ecr: (product.carbon_footprint_electricity || 0) * annualProduction,
            },
          });
        }
        
        if (transportFootprint > 0) {
          data.push({
            name: t("results.distribution.carbon.phases.transport"),
            value: transportFootprint,
            percentage: (transportFootprint / totalDisplayed) * 100,
            color: "#f59e0b",
          });
        }
        
        if (distributionFootprint > 0) {
          data.push({
            name: t("results.distribution.carbon.phases.distribution"),
            value: distributionFootprint,
            percentage: (distributionFootprint / totalDisplayed) * 100,
            color: "#0891b2",
          });
        }
      }
    }

    return data;
  }, [product, originProduct, t]);

  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-4 border rounded-lg shadow-lg max-w-xs">
          <p className="text-sm font-semibold mb-2 text-gray-900">{data.name}</p>
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-gray-600">
                {t("results.carbon.units.kgCO2e")}:
              </span>
              <span className="text-sm font-medium">
                {formatNumber(data.value)}
              </span>
            </div>
            <div className="flex items-center justify-between gap-4">
              <span className="text-sm text-gray-600">
                {t("results.carbon.units.percentage")}:
              </span>
              <span className="text-sm font-medium">
                {data.percentage.toFixed(1)}%
              </span>
            </div>
            {data.breakdown && (
              <div className="mt-3 pt-2 border-t border-gray-200">
                <p className="text-xs text-gray-500 mb-2 font-medium">
                  {t("results.distribution.carbon.breakdown.title")}:
                </p>
                {data.breakdown.materials !== undefined && (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-600">
                      {t("results.distribution.carbon.breakdown.materials")}:
                    </span>
                    <span className="text-xs font-medium">
                      {formatNumber(data.breakdown.materials)} kg CO₂e
                    </span>
                  </div>
                )}
                {data.breakdown.ecr !== undefined && (
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-xs text-gray-600">
                      {t("results.distribution.carbon.breakdown.energyResidue")}:
                    </span>
                    <span className="text-xs font-medium">
                      {formatNumber(data.breakdown.ecr)} kg CO₂e
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }
    return null;
  };

  const CustomLegend = ({ payload }: { payload?: Array<{ color: string; value: string }> }) => {
    if (!payload) return null;
    
    return (
      <div className="flex flex-wrap justify-center gap-4 mt-4">
        {payload.map((entry, index: number) => (
          <div key={`legend-${index}`} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: entry.color }}
            />
            <span className="text-sm text-gray-600">{entry.value}</span>
          </div>
        ))}
      </div>
    );
  };

  if (chartData.length === 0) {
    const activityType = product.product_flow || "extraction";
    const isTransformationProduct = activityType === "transformation";
    
    return (
      <Card className={className}>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg">
            {t("results.distribution.carbon.chart.title")}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            {isTransformationProduct ? (
              <div className="space-y-2">
                <p className="text-gray-500">
                  {t("results.distribution.carbon.chart.transformationNoData")}
                </p>
                <p className="text-sm text-gray-400">
                  {t("results.distribution.carbon.chart.transformationNoDataHint")}
                </p>
              </div>
            ) : (
              <p className="text-gray-500">
                {t("results.distribution.carbon.chart.noData")}
              </p>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg">
          {t("results.distribution.carbon.chart.title")}
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="h-[400px] w-full">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={chartData}
                cx="50%"
                cy="50%"
                outerRadius={120}
                innerRadius={40}
                paddingAngle={2}
                dataKey="value"
                animationDuration={1000}
                minAngle={1}
                label={false}
              >
                {chartData.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend content={<CustomLegend />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </CardContent>
    </Card>
  );
};
